import React, { Component } from 'react';
import WaitingFor from '../../../Choices/WaitingFor';
import FateDieOverlay from '../../../Choices/dice/FateDieOverlay';
import RandomActionDieOverlay from '../../../Choices/dice/RandomActionDieOverlay';
import DefaultEventModal, { DefaultEventComponent } from './Default';
import EndOfGameReport from './EndOfGameReport';
import { EndOfTurnEventComponent } from './EndTurn';
import { GhostWaveEventComponent } from './GhostWaveEvent';
import ReceiveSpecialGuestEvent from './ReceiveSpecialGuestEvent';
import RoundScoringReport from './RoundScoringReport';
import { StartTurnEventComponent } from './StartTurn';
import { BurnTradelanesEventComponent } from './TradelaneEvents';
import YourScoreIs from './YourScoreIs';


// Events that own their dismiss timing — they bypass DefaultEventModal,
// the display_time=0 fast-forward, and the auto-player fast-forward.
// EndOfGameReport (29) has done this since 2026-05-16; FateDieOverlay (23)
// and RandomActionDieOverlay (21) joined on the Stage-2 dice work because
// they coordinate with the DIE_ROLL_ACK (36) cosmetic ack relay.
const STICKY_EVENT_IDS = new Set([21, 23, 29]);


const EVENT_COMPONENTS = {
    1: StartTurnEventComponent,
    2: DefaultEventComponent,
    3: DefaultEventComponent,
    4: DefaultEventComponent,
    5: YourScoreIs,
    6: DefaultEventComponent,
    7: DefaultEventComponent,
    8: DefaultEventComponent,
    9: DefaultEventComponent,
    10: DefaultEventComponent,
    11: DefaultEventComponent,
    12: DefaultEventComponent,
    13: DefaultEventComponent,
    14: DefaultEventComponent,
    15: DefaultEventComponent,
    16: ReceiveSpecialGuestEvent, // receive vanguard
    17: ReceiveSpecialGuestEvent, // receive pirate
    18: ReceiveSpecialGuestEvent,  // receive seeker
    19: DefaultEventComponent,
    20: DefaultEventComponent,
    21: RandomActionDieOverlay,  // Random Action Die roll
    22: DefaultEventComponent,
    23: FateDieOverlay,  // Fate Die roll (First Player)
    24: DefaultEventComponent,
    25: DefaultEventComponent,
    26: RoundScoringReport,
    27: DefaultEventComponent,
    28: DefaultEventComponent,
    29: EndOfGameReport,
    30: EndOfTurnEventComponent,
    31: DefaultEventComponent,
    32: DefaultEventComponent,  // draw
    33: BurnTradelanesEventComponent,  // burn
    34: DefaultEventComponent,  // shift
    35: GhostWaveEventComponent,  // Ghost Wave (Manual 4.1.1 two-tier display)
    36: DefaultEventComponent,  // DIE_ROLL_ACK — sentinel; overlays listen via window event bus
}


export default class EventComponent2d extends Component {

    constructor(props) {
        super(props);
        this.state = {
            eventComponent: null,
            receivedMessage: null
        };
        this.app = props.app;
        this.game = props.app.game;
        this.library = props.app.game.library;
        this.eventQueue = [];
    }

    componentDidMount() {
        window.addEventListener('event-event', this.filterEvent);
        // Expose a queue-inspection hook so FateBringsYou (et al) can
        // tell whether a dice event is queued but not yet processed.
        // The hook is queried, not pushed — keeps the router's
        // internal state private.
        window.__eventRouterHasPendingDice = this.hasPendingDiceEvent;
        const gc = this.props.app && this.props.app.gameController;
        if (gc) gc.eventRouter = this;
    }

    componentWillUnmount() {
        if (window.__eventRouterHasPendingDice === this.hasPendingDiceEvent) {
            delete window.__eventRouterHasPendingDice;
        }
        const gc = this.props.app && this.props.app.gameController;
        if (gc && gc.eventRouter === this) gc.eventRouter = null;
    }

    // Called by GameController.drainQueue when the EOG event arrives.
    // Empties the queue and clears any currently-rendered non-EOG event
    // (a mid-tumble dice overlay, a round scoring report, etc). The EOG
    // event itself is rendered immediately afterwards by filterEvent's
    // special case, so we deliberately leave eventComponent intact if
    // EOG is already on screen (defensive — drainEvents can be called
    // twice if EndOfGameReport.componentDidMount also fires it).
    drainEvents = () => {
        this.eventQueue.length = 0;
        const currentId = this.state.receivedMessage
            && this.state.receivedMessage.package
            && this.state.receivedMessage.package.data
            && this.state.receivedMessage.package.data.event;
        if (currentId !== 29 && this.state.eventComponent) {
            this.setState({ eventComponent: null, receivedMessage: null });
        }
    };

    hasPendingDiceEvent = () => {
        const currentId = this.state.receivedMessage
            && this.state.receivedMessage.package
            && this.state.receivedMessage.package.data
            && this.state.receivedMessage.package.data.event;
        if (currentId === 21 || currentId === 23) return true;
        for (const msg of this.eventQueue) {
            const id = msg && msg.package && msg.package.data
                && msg.package.data.event;
            if (id === 21 || id === 23) return true;
        }
        return false;
    };

    filterEvent = (e) => {
        const receivedMessage = e.detail;
        const eventId = receivedMessage.package.data.event;
        const gc = this.props.app && this.props.app.gameController;

        // EOG (29) is special: drain everything else first so trailing
        // dice/orbit/burn events don't keep animating after the game is
        // over, then render the EOG report directly. drainQueue flips
        // gc.gameEnded=true; later non-EOG events get dropped below.
        if (eventId === 29) {
            if (gc && typeof gc.drainQueue === 'function') {
                gc.drainQueue();
            }
            this.eventQueue.length = 0;
            this.processMessage(receivedMessage);
            return;
        }

        if (gc && gc.gameEnded) return;

        let processEvent = true;
        if (receivedMessage.package.data.game_player) {
            let gamePlayerData = this.props.gamePlayerData;
            if (!gamePlayerData || receivedMessage.package.data.game_player !== gamePlayerData.id) {
                processEvent = false;
            }
        }

        // Next-die-cancels-current invariant
        // ([[project_hsh_dice_modal_never_blocks_on_next_roll]]). When a
        // dice event (Random Action = 21, Fate = 23) arrives and a dice
        // overlay is already on screen — at ANY phase: idle, tumbling, or
        // settled-hold — force-finish the in-flight overlay and mount the
        // new die immediately. We never queue dice behind dice: AI-on-auto
        // generates rolls faster than the 1100ms tumble + 600ms hold can
        // absorb, and observers must keep pace with the engine, not with
        // the previous die's animation. The pre-empt applies unconditionally
        // to the legitimate "engine fires the next die because the previous
        // one resolved" case. Manual §8.0/§12: the First Player is the sole
        // roller for every die, so a new die arriving while that roller
        // holds Wait is structurally impossible — Wait blocks the engine,
        // and there is no second roller to spawn another die (I4). The
        // unconditional pre-empt is therefore also defensively safe against
        // a forged/anomalous event off the wire.
        //
        // Queue policy on pre-empt: KEEP the queue. Any non-dice event
        // already waiting behind the pre-empted die was waiting on the
        // overlay slot, not on that specific die; it stays queued and the
        // new die simply takes the live slot. Clearing the queue would drop
        // legitimately-pending non-dice surfaces (round scoring, etc.) for
        // no benefit.
        const currentEventId = this.state.receivedMessage
            && this.state.receivedMessage.package
            && this.state.receivedMessage.package.data
            && this.state.receivedMessage.package.data.event;
        const newIsDice = (eventId === 21 || eventId === 23);
        const currentIsDice = (
            currentEventId === 21 || currentEventId === 23);
        if (processEvent && newIsDice && currentIsDice
                && this.state.eventComponent) {
            this.setState(
                { eventComponent: null, receivedMessage: null },
                () => this.processMessage(receivedMessage));
            return;
        }

        if (processEvent) this.queueMessage(receivedMessage);
    };

    queueMessage = (receivedMessage) => {
        this.eventQueue.push(receivedMessage);
        if (!this.state.eventComponent) this.processQueue();
    };

    processQueue = () => {
        let nextMessage = this.eventQueue.shift();
        if (nextMessage) {
            this.processMessage(nextMessage);
        }
    };

    processMessage = (receivedMessage) => {
        // console.log('EventComponent2d.processMessage', receivedMessage);
        let eventId = receivedMessage.package.data.event;

        let event = receivedMessage.package.event;
        let soundEntryNumber = event.sound_library_entry_number;
        this.library.soundLibrary.play(soundEntryNumber);

        // Sticky events (End of Game = 29, dice overlays = 21/23) own
        // their dismiss timing, even if the Event model's display_time
        // is 0 or the seat is on autopilot. The dice overlays still
        // render in auto-mode because the rolling player's overlay is
        // what fires the DieRollAck relay that unblocks observers.
        if (!STICKY_EVENT_IDS.has(eventId)) {
            if (event.type.display_time === 0) {
                this.eventComplete();
                return;
            }
            if (this.game.automaticPlayerIsEngaged()) {
                this.eventComplete();
                return;
            }
        }

        let eventComponent = EVENT_COMPONENTS[eventId];
        this.setState({
            eventComponent: eventComponent,
            receivedMessage: receivedMessage });
    };

    eventComplete = () => {
        // After EOG fires drainQueue, the previously-active overlay
        // (e.g. a FateDieOverlay mid-tumble) may still be holding a
        // setTimeout that eventually invokes its captured eventComplete
        // closure. Honoring that callback now would tear down the EOG
        // report we just mounted. Drop late completions silently.
        const gc = this.props.app && this.props.app.gameController;
        if (gc && gc.gameEnded) return;
        this.setState({ eventComponent: null, receivedMessage: null },
            this.processQueue);
    }

    render() {
        if (!this.state.eventComponent) {
            return <></>;
        }

        let event = this.state.receivedMessage.package.event;
        const eventId = this.state.receivedMessage.package.data.event;

        // Sticky events bypass DefaultEventModal so the component owns
        // its dismiss (no auto-close, no body-click). End of Game (29)
        // since 2026-05-16; FateDieOverlay (23) and RandomActionDieOverlay
        // (21) since the Stage-2 dice work.
        if (STICKY_EVENT_IDS.has(eventId)) {
            const EventComponent = this.state.eventComponent;
            return <div id={'EventComponent'}>
                <EventComponent
                    receivedMessage={this.state.receivedMessage}
                    event={event}
                    eventComplete={this.eventComplete}
                    {...this.props}
                />
            </div>;
        }

        return <div id={'EventComponent'}>
            <DefaultEventModal
                className={'event-base'}
                receivedMessage={this.state.receivedMessage}
                eventComponent={this.state.eventComponent}
                eventComplete={this.eventComplete}
                {...this.props}
            />
        </div>;
    }

}
